import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api, type AuthAuditRow, type ImpactRow } from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import styles from "./SafetyPage.module.css";

/** Severity → CSS-module key (pure; status tokens per fusion §5, never invent colours). */
export function severityKey(severity: string): "sevSevero" | "sevModerado" | "sevLeve" {
  switch (severity) {
    case "SEVERO": return "sevSevero";
    case "MODERADO": return "sevModerado";
    default: return "sevLeve";
  }
}

function severityClass(severity: string): string {
  return styles[severityKey(severity)];
}

/** Locale-aware event-kind label (falls back to the raw kind). */
export function kindLabel(kind: string): string {
  return i18n.t(`safety.kind.${kind}`, { defaultValue: kind });
}

function fmtTs(ms: number, locale: string): string {
  return new Date(ms).toLocaleString(locale);
}

function magnitude(r: ImpactRow): string {
  if (r.event_kind === "crash" && r.shock_g != null) return `${Math.round(r.shock_g)} g`;
  if (r.event_kind === "harsh_accel" && r.net_g != null) return `${r.net_g.toFixed(2)} g`;
  if (r.event_kind === "tilt" && r.pitch != null) return `${Math.round(Math.max(Math.abs(r.pitch), Math.abs(r.roll ?? 0)))}°`;
  return "—";
}

export default function SafetyPage() {
  const { t, i18n: i18nInst } = useTranslation();
  const siteId = useSiteId();
  // Window frozen at mount (lazy init keeps render pure and the queryKey stable —
  // a per-render Date.now() re-keyed the queries on every unrelated re-render).
  const [to] = useState(() => Date.now());
  const from = to - 24 * 3600 * 1000;   // last 24h

  const impacts = useQuery({
    queryKey: ["impacts", siteId, from, to],
    queryFn: () => api.impacts(siteId, from, to),
    enabled: !!siteId,
  });
  const audit = useQuery({
    queryKey: ["authAudit", siteId, from, to],
    queryFn: () => api.authAudit(siteId, from, to),
    enabled: !!siteId,
  });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t("safety.title")}</h1>
      <p className={styles.subtitle}>{t("safety.subtitle")}</p>

      <section className={styles.section}>
        <h2 className={styles.h2}>{t("safety.driving")}</h2>
        {impacts.isLoading && <p className={styles.muted}>{t("common.loading")}</p>}
        {impacts.data && impacts.data.length === 0 && <p className={styles.muted}>{t("safety.noImpacts")}</p>}
        {impacts.data && impacts.data.length > 0 && (
          <table className={styles.table}>
            <thead><tr><th>{t("common.col.when")}</th><th>{t("safety.col.type")}</th><th>{t("safety.col.severity")}</th><th>{t("safety.col.magnitude")}</th><th>{t("common.col.equipment")}</th></tr></thead>
            <tbody>
              {impacts.data.map((r: ImpactRow, i: number) => (
                <tr key={i}>
                  <td>{fmtTs(r.ts_ms, i18nInst.language)}</td>
                  <td>{kindLabel(r.event_kind)}</td>
                  <td><span className={`${styles.pill} ${severityClass(r.severity)}`}>{t(`safety.sev.${r.severity}`, { defaultValue: r.severity })}</span></td>
                  <td>{magnitude(r)}</td>
                  <td>{r.asset_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>{t("safety.access")}</h2>
        {audit.isLoading && <p className={styles.muted}>{t("common.loading")}</p>}
        {audit.data && audit.data.length === 0 && <p className={styles.muted}>{t("safety.noAccess")}</p>}
        {audit.data && audit.data.length > 0 && (
          <table className={styles.table}>
            <thead><tr><th>{t("common.col.when")}</th><th>{t("safety.col.event")}</th><th>{t("safety.col.card")}</th><th>{t("common.col.equipment")}</th></tr></thead>
            <tbody>
              {audit.data.map((r: AuthAuditRow, i: number) => (
                <tr key={i}>
                  <td>{fmtTs(r.ts_ms, i18nInst.language)}</td>
                  <td>{kindLabel(r.event_kind)}</td>
                  <td>{r.driver_uid || "—"}</td>
                  <td>{r.asset_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
